/**
 * Menu → Deals → Upsells (F-DEAL-N4): the per-category default suggestions,
 * plus a read-only list of items that set their own.
 */
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Edit, Info } from 'lucide-react'
import apiClient from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useCurrency } from '@/contexts/CurrencyContext'
import { toastHelpers } from '@/lib/toast-helpers'
import type { Category, Product } from '@/types'
import type { UpsellCatalogue } from '@/types/deals'
import { TargetPicker, UPSELL_ADMIN_QUERY_KEY, useDealsCatalog } from './DealsControls'
import { errorText } from './dealsText'
import { UpsellSettingCard } from './UpsellSettingCard'

const MAX_SUGGESTIONS = 4

function CategoryUpsellRow({
  category,
  prompt,
  suggested,
  products,
  money,
}: {
  category: Category
  prompt: string
  suggested: string[]
  products: Product[]
  money: (v: number) => string
}) {
  const qc = useQueryClient()
  const [p, setP] = useState(prompt)
  const [ids, setIds] = useState<string[]>(suggested)
  const savedKey = `${prompt}|${suggested.join(',')}`
  useEffect(() => {
    setP(prompt)
    setIds(suggested)
    // Re-seed only when the saved value changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey])
  const dirty = p.trim() !== prompt.trim() || ids.join(',') !== suggested.join(',')

  const save = useMutation({
    mutationFn: () => apiClient.putCategoryUpsell(String(category.id), { prompt: p.trim(), suggested_product_ids: ids }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: UPSELL_ADMIN_QUERY_KEY })
      qc.invalidateQueries({ queryKey: ['menu-rules'] })
      toastHelpers.success('Suggestions saved', category.name)
    },
    onError: (e) => toastHelpers.error('Could not save suggestions', errorText(e)),
  })

  return (
    <div className="grid gap-3 p-4 lg:grid-cols-[12rem_1fr_auto] lg:items-start">
      <div className="pt-2 text-sm font-medium">{category.name}</div>
      <div className="space-y-2">
        <Input
          value={p}
          maxLength={80}
          aria-label={`Prompt for ${category.name}`}
          placeholder="Prompt (optional), e.g. Add a drink?"
          onChange={(e) => setP(e.target.value)}
        />
        <TargetPicker
          products={products}
          categories={[]}
          productIds={ids}
          categoryIds={[]}
          allowCategories={false}
          max={MAX_SUGGESTIONS}
          addLabel={ids.length === 0 ? 'Add suggestions' : 'Add another'}
          onChange={(next) => setIds(next)}
          money={money}
        />
      </div>
      <Button type="button" size="sm" variant={dirty ? 'default' : 'outline'} disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
        {save.isPending ? 'Saving…' : 'Save'}
      </Button>
    </div>
  )
}

export function UpsellsPane({ onEditProduct }: { onEditProduct?: (p: Product) => void }) {
  const { formatCurrency } = useCurrency()
  const catalog = useDealsCatalog()
  const query = useQuery({
    queryKey: UPSELL_ADMIN_QUERY_KEY,
    queryFn: async () => (await apiClient.getUpsellAdmin()).data as UpsellCatalogue,
    staleTime: 30_000,
  })
  const data = query.data
  const productById = useMemo(() => new Map(catalog.all.map((p) => [String(p.id), p])), [catalog.all])

  const ownRows = useMemo(() => {
    if (!data) return []
    const ids = new Set<string>([
      ...Object.keys(data.product_upsells ?? {}),
      ...Object.entries(data.product_modes ?? {})
        .filter(([, m]) => m && m !== 'inherit')
        .map(([id]) => id),
      ...Object.keys(data.product_prompts ?? {}).filter((id) => (data.product_prompts[id] ?? '').trim() !== ''),
    ])
    return [...ids]
      .map((id) => ({
        id,
        product: productById.get(id),
        mode: data.product_modes?.[id] ?? 'inherit',
        prompt: data.product_prompts?.[id] ?? '',
        suggested: data.product_upsells?.[id] ?? [],
      }))
      .sort((a, b) => (a.product?.name ?? '').localeCompare(b.product?.name ?? ''))
  }, [data, productById])

  return (
    <div className="space-y-6">
      {data && !data.config.enabled && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-300">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            Upsell suggestions are off, so nothing below is shown yet. Turn them on here or in{' '}
            <Link to="/admin/settings" search={{ section: 'general' }} className="font-medium underline underline-offset-2">
              Settings
            </Link>
            . You can set up suggestions before turning them on.
          </span>
        </div>
      )}

      <UpsellSettingCard />

      <div className="space-y-3">
        <div className="max-w-2xl space-y-1">
          <h3 className="font-semibold">Suggestions by category</h3>
          <p className="text-sm text-muted-foreground">
            When an item from a category is added, suggest up to {MAX_SUGGESTIONS} items, for example a drink with every burger. An item can override this on its own form.
          </p>
        </div>
        {query.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : query.isError || !data ? (
          <p className="text-sm text-destructive">Could not load upsells. Refresh the page to try again.</p>
        ) : catalog.categories.length === 0 ? (
          <p className="text-sm text-muted-foreground">Create a category first.</p>
        ) : (
          <div className="divide-y rounded-lg border">
            {catalog.categories.map((c) => (
              <CategoryUpsellRow
                key={c.id}
                category={c}
                prompt={data.category_prompts?.[String(c.id)] ?? ''}
                suggested={data.category_upsells?.[String(c.id)] ?? []}
                products={catalog.all}
                money={formatCurrency}
              />
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="max-w-2xl space-y-1">
          <h3 className="font-semibold">Items with their own suggestions</h3>
          <p className="text-sm text-muted-foreground">These items ignore their category’s list. Change them on the item’s form.</p>
        </div>
        {data && ownRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">None — every item uses its category’s suggestions.</p>
        ) : (
          <div className="divide-y rounded-lg border">
            {ownRows.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 p-4">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{r.product?.name ?? 'Removed item'}</span>
                    {r.mode === 'off' && <Badge variant="neutral-surface">Suggestions off</Badge>}
                    {r.mode === 'on' && <Badge variant="info-surface">Own list</Badge>}
                  </div>
                  {r.prompt && <p className="text-sm text-muted-foreground">“{r.prompt}”</p>}
                  {r.mode !== 'off' && r.suggested.length > 0 && (
                    <p className="text-sm text-muted-foreground">
                      Suggests: {r.suggested.map((id) => productById.get(id)?.name ?? 'removed item').join(', ')}
                    </p>
                  )}
                </div>
                {r.product && onEditProduct && (
                  <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => onEditProduct(r.product as Product)}>
                    <Edit className="h-4 w-4" />
                    Edit item
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
